import { memo, useCallback } from 'react';
import type { RootState } from 'infrastructure/store';
import { useSimpleRequest, useTypedSelector } from 'hooks';
import { MOR } from 'consts/general';
import { SetupIdentifier } from 'consts/setups';
import { Paginate } from 'consts/table';
import { PER_PAGE_SIZE } from 'consts/transactions';
import { assertStrategies, when } from 'util/support';
import { isFormChanged } from 'util/forms';
import { RequestQueryMapper } from 'util/request-query-mapper';
import { getPromiseSettledResourceResult } from 'util/resource';
import type { Setup, SetupResponse, WithClonnable } from 'features/setups/types';
import { selectIsSetupsLoading } from 'features/setups/selectors';
import useSetupsActions from "features/setups/useActions";
import { updateOrCreateSetupGrupPivot } from 'features/setups/api';
import {
    selectSetupGroupNames,
    selectSetupActiveCompany as selectSetupActiveCompanySelector
} from 'features/general/selectors';
import { withSx } from 'ui/atoms/Modal';
import When from 'ui/support/When';
import ActionPanel from 'ui/molecules/ActionPanel';
import {
    useResourceContext,
    ResourceDetail,
    type ResourceDetailProps
} from "ui/widgets/Resource";
import { GeneralSetupForm } from 'ui/forms/Setup';
import useGeneralSetups from "./useGeneralSetups";

const GeneralSetupsDetails = (props: ResourceDetailProps) => {
    const { resource, onClose } = useResourceContext<WithClonnable<Setup<string>>>();

    const renderDialogTitle = (resource = {} as WithClonnable) => (
        when(
            resource.clonnable,
            `Clone ${resource.identifier} setup`,
            `Update ${resource.identifier} setup`
        )
    );

    return (
        <ResourceDetail
            {...props}
            titleSlot={renderDialogTitle(resource)}
            disableCloseOnBackdropClick
            onClose={onClose}
            DialogProps={withSx({
                width: 'fit-content'
            })}
            DialogTitleProps={{
                sx: {
                    mr: 5
                }
            }}
        >
            <When
                if={Boolean(resource)}
                then={useCallback(() => (
                    <GeneralSetupFormComponent />
                ), [])}
            />
        </ResourceDetail>
    );
};

export default memo(GeneralSetupsDetails);

function GeneralSetupFormComponent() {
    const { resource, onClose } = useResourceContext<WithClonnable<Setup<SetupIdentifier>>>();
    const isBootstrapDataLoading = useTypedSelector(selectIsSetupsLoading(resource.identifier));
    const { getResource } = useGeneralSetups();
    const { updateOrCreateSetup } = useSetupsActions();
    const query = useSimpleRequest();

    const getOverrideProps = (resource = {} as WithClonnable): GeneralSetupForm.SetupProps['overrideProps'] => {
        const editableProps = {
            disabled: false
        };

        return {
            MOR: when(
                resource.clonnable,
                editableProps
            ),
            identifier: when(
                assertStrategies(
                    new Set([
                        () => resource.clonnable,
                        () => Object.is(
                            resource.MOR,
                            MOR.Default
                        )
                    ])
                ),
                editableProps
            )
        };
    };

    const formProps: GeneralSetupForm.SetupForm = {
        initialValues: GeneralSetupForm.getInitialValues(
            when(
                resource.clonnable,
                () => ({
                    ...resource,
                    isDeletable: null,
                    createdBySystem: null,
                    MOR: null
                }),
                resource
            )
        ),
        isBootstrapDataLoading: isBootstrapDataLoading,
        bootstrapData: {},
        onSaveOrCreate: async ({
            setupValue,
            identifier,
            createdBySystem,
            isDeletable,
            valueType,
            obs,
            MOR,
            coreId,
            groupName,
        }, formHelpers) => {
            const [request, result] = updateOrCreateSetup({
                ...when(
                    resource.clonnable,
                    {},
                    () => ({
                        isDeletable,
                        createdBySystem,
                        coreId
                    })
                ),
                valueType,
                identifier,
                obs,
                MOR: MOR ?? resource.MOR,
                setupValue: setupValue!.serialize(setupValue)
            });

            const [setupsSettledResponse] = await Promise.allSettled([
                request,
                when(
                    groupName,
                    () => query(() => updateOrCreateSetupGrupPivot({
                        groupName,
                        identifier
                    }))
                )
            ]);

            when(
                getPromiseSettledResourceResult(
                    setupsSettledResponse as PromiseSettledResult<SetupResponse<string>>
                )?.success,
                () => {
                    getResource(
                        RequestQueryMapper.from()
                            .contains(Paginate.perPage, `${PER_PAGE_SIZE}`)
                            .toString()
                    )
                    onClose();
                    formHelpers!.resetForm();
                });

            return result;
        }
    };

    const form = GeneralSetupForm.useForm(formProps);

    const selectSetupActiveCompany = (state: RootState) => (
        selectSetupActiveCompanySelector(state)
            .filter(company => when(
                resource.clonnable,
                () => !Object.is(
                    resource.MOR,
                    company
                ),
                true
            ))
    );
    const selectSetUpGroupName = (state: RootState) => (
        selectSetupGroupNames(state))

    return (
        <GeneralSetupForm.Form
            {...form}
            {...formProps}
            overrideProps={getOverrideProps(resource)}
            selectSetupActiveCompany={selectSetupActiveCompany}
            setUpGroupNameOptions={selectSetUpGroupName}
        >
            <ActionPanel
                onSave={async () => { }}
                onCancel={onClose}
                SaveActionProps={{
                    type: 'submit',
                    disabled: !form.isValid || !isFormChanged(form),
                    loading: form.isSubmitting
                }}
            />
        </GeneralSetupForm.Form>
    );
}
